import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { PDFViewer } from "@react-pdf/renderer";
import { tokens } from "../../../theme";
import {
  listHotelInvoicesDetails,
  resetHotelInvoicesDetails,
  updateHotelInvoicesDetails,
} from "../../../store/actions";
import InvoicePDF from "./InvoicePDF";
import { getTaxRateForAmount } from "../../../utils";
import moment from "moment";
import { toast } from "react-toastify";

export default function BookingBillDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [showPreview, setShowPreview] = useState(false);
  const [editedRoomCharges, setEditedRoomCharges] = useState([]);
  const [totals, setTotals] = useState({
    amount: 0,
    sgst: 0,
    cgst: 0,
    total: 0,
  });
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [customer, setCustomer] = useState({});
  const [bookingDetails, setBookingDetails] = useState({
    checkin_date: "",
    checkout_date: "",
    booking_id: "",
    from: "",
    inv_no: "",
    paid_amount: 0,
  });

  const { hotelInvoivesDetails, hotelProfile } = useSelector((state) => ({
    hotelInvoivesDetails: state.admin.hotelInvoivesDetails,
    hotelProfile: state.registration.hotelProfile,
  }));

  useEffect(() => {
    document.title = "Booking Invoice Details";
    if (id) {
      dispatch(resetHotelInvoicesDetails());
      dispatch(listHotelInvoicesDetails({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (hotelInvoivesDetails) {
      const formatted =
        hotelInvoivesDetails.room_charges?.map((item) => ({
          ...item,
          date: item.date
            ? new Date(item.date).toISOString().split("T")[0]
            : "",
        })) || [];

      setEditedRoomCharges(formatted);
      setCustomer(hotelInvoivesDetails.customer || {});
      setPaymentStatus(hotelInvoivesDetails.pmt_status || "unpaid");
      setBookingDetails({
        checkin_date: hotelInvoivesDetails.checkin_date
          ? moment(hotelInvoivesDetails.checkin_date * 1000).format(
              "YYYY-MM-DD"
            )
          : "",
        checkout_date: hotelInvoivesDetails.checkout_date
          ? moment(hotelInvoivesDetails.checkout_date * 1000).format(
              "YYYY-MM-DD"
            )
          : "",
        booking_id: hotelInvoivesDetails.booking_id || "",
        from: hotelInvoivesDetails.from || "",
        inv_no: hotelInvoivesDetails.inv_no || "",
        paid_amount: hotelInvoivesDetails.paid_amount || 0,
      });
    }
  }, [hotelInvoivesDetails]);

  useEffect(() => {
    let amount = 0;
    let sgst = 0;
    let cgst = 0;

    editedRoomCharges.forEach((item) => {
      const isTaxRow = (item.sgst || 0) > 0 || (item.cgst || 0) > 0;

      if (!isTaxRow) {
        amount += item.amount || 0;
      }

      sgst += item.sgst || 0;
      cgst += item.cgst || 0;
    });

    setTotals({
      amount: parseFloat(amount.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      total: parseFloat((amount + sgst + cgst).toFixed(2)),
    });
  }, [editedRoomCharges]);

  const pdfData = useMemo(() => {
    const sortedRoomCharges = [...editedRoomCharges].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    return {
      ...hotelInvoivesDetails,
      ...totals,
      room_charges: sortedRoomCharges,
      ...bookingDetails,
      customer,
      pmt_status: paymentStatus,
      hotelDetails: {
        name: hotelProfile?.name || "",
        address: hotelProfile?.address || "",
        phone: hotelProfile?.phone || "",
        mobile: hotelProfile?.mobile || "",
        email: hotelProfile?.email || "",
        gstin: hotelProfile?.gstin || "",
        logo: hotelProfile?.logo || "",
        bank_details: hotelProfile?.bank_details || {},
        cin: hotelProfile?.cin || "",
        pan: hotelProfile?.pan || "",
        gst_enabled: hotelProfile?.gst_enabled || false,
      },
      checkin_date: bookingDetails.checkin_date
        ? Math.floor(new Date(bookingDetails.checkin_date).getTime() / 1000)
        : null,
      checkout_date: bookingDetails.checkout_date
        ? Math.floor(new Date(bookingDetails.checkout_date).getTime() / 1000)
        : null,
    };
  }, [
    hotelInvoivesDetails,
    totals,
    editedRoomCharges,
    customer,
    paymentStatus,
    hotelProfile,
  ]);

  const {
    inv_no,
    inv_date,
    checkin_date,
    checkout_date,
    booking_id,
    from,
    paid_amount,
  } = hotelInvoivesDetails || {};

  const handleFieldChange = (index, field, value) => {
    const updated = [...editedRoomCharges];
    if (!updated[index]) return;

    updated[index][field] = value;

    if (field === "amount") {
      const item = updated[index];
      if (!item) return;
      if (item.cgst) {
        updated[index]["cgst"] = value;
      }
       if (item.sgst) {
        updated[index]["sgst"] = value;

      }

    }

    // if (field === "amount") {
    //   const groupId = updated[index].groupId;
    //   const amount = parseFloat(value) || 0;

    //   const taxRate = getTaxRateForAmount(amount);
    //   const halfTax = taxRate / 2;
    //   const sgstValue = parseFloat(((amount * halfTax) / 100).toFixed(2));
    //   const cgstValue = sgstValue;

    //   updated.forEach((item, i) => {
    //     if (item.groupId === groupId) {
    //       const isSGST = item.particulars.toLowerCase().includes("sgst");
    //       const isCGST = item.particulars.toLowerCase().includes("cgst");
    //       const dayMatch = item.particulars.match(/Day\s*\d+/i);
    //       const dayText = dayMatch ? dayMatch[0] : "";

    //       if (isSGST) {
    //         updated[i].amount = sgstValue;
    //         updated[i].sgst = sgstValue;
    //         updated[i].cgst = 0;

    //         // ✅ Update particulars with correct tax %
    //         updated[
    //           i
    //         ].particulars = `SGST @ ${halfTax}% for ${item.roomNumber} (${dayText})`;
    //       }

    //       if (isCGST) {
    //         updated[i].amount = cgstValue;
    //         updated[i].cgst = cgstValue;
    //         updated[i].sgst = 0;

    //         // ✅ Update particulars with correct tax %
    //         updated[
    //           i
    //         ].particulars = `CGST @ ${halfTax}% for ${item.roomNumber} (${dayText})`;
    //       }
    //     }
    //   });
    // }

    setEditedRoomCharges(updated);
  };

  const showGSTToast = (item) => {
    toast.info("Please verify that the GST values are correct before saving the amount.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    });
};


  const handleBookingChange = (field, value) => {
    setBookingDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (field, value) => {
    setCustomer((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedInvoice = {
      ...hotelInvoivesDetails,
      _id: id,
      customer,
      pmt_status: paymentStatus,
      room_charges: editedRoomCharges,
      ...totals,
      ...bookingDetails,
      checkin_date: bookingDetails.checkin_date
        ? Math.floor(new Date(bookingDetails.checkin_date).getTime() / 1000)
        : null,
      checkout_date: bookingDetails.checkout_date
        ? Math.floor(new Date(bookingDetails.checkout_date).getTime() / 1000)
        : null,
    };

    // Dispatch action to save the updated invoice
    dispatch(updateHotelInvoicesDetails(updatedInvoice));
  };

  const formatDate = (dateValue) =>
    new Date(dateValue).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (!hotelInvoivesDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h5" gutterBottom>
            Invoice #{inv_no}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Date: {inv_date ? formatDate(inv_date * 1000) : "N/A"}
          </Typography>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setShowPreview(true)}
            >
              Preview PDF
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        {/* Left Column - Customer */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Bill To:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                value={customer.name || ""}
                onChange={(e) => handleCustomerChange("name", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile"
                value={customer.mobile || ""}
                onChange={(e) => handleCustomerChange("mobile", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                value={customer.email || ""}
                onChange={(e) => handleCustomerChange("email", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={customer.address || ""}
                onChange={(e) =>
                  handleCustomerChange("address", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="GST No"
                value={customer.gst_no || ""}
                onChange={(e) => handleCustomerChange("gst_no", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Booking */}
        {/* Right Column - Booking */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Booking Details:
          </Typography>

          <Grid container spacing={2}>
            {/* Check-in */}
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Check-in"
                value={bookingDetails.checkin_date}
                onChange={(e) =>
                  handleBookingChange("checkin_date", e.target.value)
                }
                size="small"
                fullWidth
              />
            </Grid>

            {/* Check-out */}
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Check-out"
                value={bookingDetails.checkout_date}
                onChange={(e) =>
                  handleBookingChange("checkout_date", e.target.value)
                }
                size="small"
                fullWidth
              />
            </Grid>

            {/* Booking ID */}
            <Grid item xs={6}>
              <TextField
                label="Booking ID"
                value={bookingDetails.booking_id}
                onChange={(e) =>
                  handleBookingChange("booking_id", e.target.value)
                }
                size="small"
                fullWidth
                disabled={true}
              />
            </Grid>

            {/* Booking From */}
            <Grid item xs={6}>
              <TextField
                label="Booking From"
                value={bookingDetails.from}
                onChange={(e) => handleBookingChange("from", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Invoice No */}
            <Grid item xs={6}>
              <TextField
                label="Invoice No"
                value={bookingDetails.inv_no}
                onChange={(e) => handleBookingChange("inv_no", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Paid Amount */}
            <Grid item xs={6}>
              <TextField
                type="number"
                label="Advace Paid Amount"
                value={bookingDetails.paid_amount}
                onChange={(e) =>
                  handleBookingChange(
                    "paid_amount",
                    parseFloat(e.target.value) || 0
                  )
                }
                size="small"
                fullWidth
                disabled={true}
              />
            </Grid>

            {/* Payment Status */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-status-label">
                  Payment Status
                </InputLabel>
                <Select
                  labelId="payment-status-label"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Room Charges & Services
      </Typography>
      <TableContainer sx={{ bgcolor: colors.primary[400], p: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell sx={{ width: "40%" }}>Particulars</TableCell>
              <TableCell>#SAC</TableCell>
              <TableCell>Amount (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editedRoomCharges.map((item, index) => (
              <TableRow key={item.groupId ? `${item.groupId}-${index}` : index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField
                    type="date"
                    value={item.date}
                    onChange={(e) =>
                      handleFieldChange(index, "date", e.target.value)
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={item.particulars || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "particulars", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={item.sac_code || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "sac_code", e.target.value)
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={item.amount || 0}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "amount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    disabled={true}
                    // disabled={
                    //   // !!item.sgst ||
                    //   // !!item.cgst ||
                    //   // item.particulars?.toLowerCase().includes("activities") ||
                    //   item.roomNumber === "" | null| item.roomNumber === undefined
                    // }
                    // onFocus={() => showGSTToast(item)}
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        mt={2}
      >
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" sx={{ bgcolor: colors.primary[400], p: 2 }}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Subtotal:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.amount.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography>SGST:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.sgst.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography>CGST:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.cgst.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Always show Grand Total first */}
              <Grid item xs={6}>
                <Typography variant="h6">Grand Total:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="h6">₹{totals.total.toFixed(2)}</Typography>
              </Grid>

              {/* If advance payment exists, show Advance Paid and Balance Due */}
              {paid_amount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography>Advance Paid:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography color="success.main">
                      - ₹{paid_amount.toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="h6">Balance Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography variant="h6">
                      ₹{(totals.total - paid_amount).toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        maxWidth="lg"
        fullWidth
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <DialogActions sx={{ justifyContent: "space-between", p: "8px 24px" }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, height: "100vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF data={pdfData} />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
